"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import { Knopf } from "../knopf";
import { Textfeld } from "./textfeld";
import { Optionsgruppe } from "./optionsgruppe";

/**
 * Dauerwahl (Betreiber 12.10.2026, Entscheid „K2 + D1“; Design-Guide `formularfelder.md`): gängige Minutenwerte als Segmentleiste,
 * dazu „Frei“ mit einem Zahlenfeld (Ziffernblock, Minus/Plus in Fünferschritten). Der Wert ist immer ein Text in Minuten
 * (wie das Feld der Action); die Prüfung (Bereich) bleibt beim Aufrufer, der den Satz über `fehler` am Feld zeigt (r100).
 *
 * - Ein Bestandswert, der keine Voreinstellung ist (z. B. 50), steht als „Frei“ mit dem Wert im Zahlenfeld.
 * - `keineAngabe` (Serie ohne Dauer): leerer Wert ist erlaubt und steht als „Frei“ mit leerem Zahlenfeld; der Hilfesatz sagt es.
 * - Der Baustein rechnet nichts und kennt keine Endzeit (die zeigt `Endzeitzeile`).
 *
 * Zusammensetzung aus `Optionsgruppe variante="segment"` (Abweichung AW-012) + `Textfeld` + `Knopf`; Zahlenfeld mit Minus/Plus als Eigenbau
 * ohne das Preline-Plugin `input-number` (Abweichung AW-044), Kurzbezeichnung „Frei“ statt „Andere …“, weil sechs Segmente bei 390 px
 * sonst umbrechen.
 */
/** Lucide „Clock“, „Minus“, „Plus“ als Inline-SVG (das Paket bindet lucide-react nicht ein, wie `zeilen-symbole.tsx`). */
function Zeichnung({ children }: { children: ReactNode }) {
  return (
    <svg
      className="shrink-0 size-4"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
const Clock = () => (
  <Zeichnung>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Zeichnung>
);
const Minus = () => (
  <Zeichnung>
    <path d="M5 12h14" />
  </Zeichnung>
);
const Plus = () => (
  <Zeichnung>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </Zeichnung>
);

const VORGABEN = [15, 30, 45, 60, 90] as const;
const SCHRITT = 5;
const MAXIMUM_STANDARD = 1440;
const FREI = "frei";

export interface DauerwahlEigenschaften {
  id?: string;
  /** Beschriftung der Gruppe (Legende). */
  label?: string;
  /** Name der Radiogruppe (Pflicht, eindeutig im Formular). */
  name: string;
  /** Minuten als Text; `""` = keine Angabe. */
  wert: string;
  beiAenderung: (wert: string) => void;
  /** Voreinstellungen in Minuten (Standard 15, 30, 45, 60, 90). */
  voreinstellungen?: readonly number[];
  /** Leerer Wert ist erlaubt („Keine Angabe“, nur Serie ohne Dauer). */
  keineAngabe?: boolean;
  /** Obergrenze der Plus-Schritte in Minuten (Standard 1440, der Bereich aller Aufrufer); Plus geht nie darüber hinaus. */
  maximum?: number;
  /** Feldfehler (F3): Satz am Zahlenfeld. */
  fehler?: string;
  deaktiviert?: boolean;
}

export function Dauerwahl({
  id,
  label = "Dauer in Minuten",
  name,
  wert,
  beiAenderung,
  voreinstellungen = VORGABEN,
  keineAngabe = false,
  maximum = MAXIMUM_STANDARD,
  fehler,
  deaktiviert = false,
}: DauerwahlEigenschaften) {
  const istVorgabe = (w: string) => voreinstellungen.some((m) => String(m) === w);
  // „Frei“ ist ein eigener Zustand: die Wahl bleibt, auch wenn der getippte Wert zufällig eine Voreinstellung ist.
  const [frei, setFrei] = useState(() => (wert === "" ? keineAngabe : !istVorgabe(wert)));
  const gewaehlt = frei ? FREI : wert;

  const optionen = [...voreinstellungen.map((m) => ({ wert: String(m), label: String(m) })), { wert: FREI, label: "Frei" }];

  const waehle = (neu: string) => {
    if (neu === FREI) {
      setFrei(true);
      return;
    }
    setFrei(false);
    beiAenderung(neu);
  };
  const schritt = (richtung: 1 | -1) => {
    const aktuell = Number(wert);
    const basis = Number.isInteger(aktuell) && aktuell > 0 ? aktuell : 0;
    // Auf das nächste Vielfache von 5 in Richtung des Knopfes; nie unter 1.
    // Nach oben nie über `maximum` (Befund B6b: 1440 + Plus ergab 1445, ein Wert, den die Prüfung dann ablehnt).
    const ziel = richtung === 1 ? Math.min(maximum, (Math.floor(basis / SCHRITT) + 1) * SCHRITT) : Math.max(1, Math.min(maximum, (Math.ceil(basis / SCHRITT) - 1) * SCHRITT));
    beiAenderung(String(ziel));
  };

  // Nicht `${id}-frei`: so heißt schon das Segment „Frei“ der Optionsgruppe (Id = `${id}-${wert}`).
  const feldId = id ? `${id}-zahl` : undefined;
  return (
    <div>
      <Optionsgruppe
        id={id}
        legende={label}
        name={name}
        variante="segment"
        optionen={optionen}
        wert={gewaehlt}
        beiAenderung={waehle}
        deaktiviert={deaktiviert}
        fehler={frei ? undefined : fehler}
      />
      {frei && (
        <div className="mt-3 flex items-start gap-x-2">
          {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-checkbox-and-preference-controls/numeric-inputs-with-switches (nur die Zahlenfeld-Mechanik als Idee; Eigenbau ohne Plugin, Fünferschritte, Abweichung AW-044) */}
          <Knopf
            nurSymbol
            variante="sekundaer"
            groesse="lg"
            type="button"
            label="5 Minuten kürzer"
            symbol={<Minus />}
            disabled={deaktiviert}
            onClick={() => schritt(-1)}
          />
          <div className="min-w-0 grow max-w-xs">
            <Textfeld
              id={feldId}
              label={`${label}, freier Wert`}
              labelVersteckt
              name={`${name}-frei`}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={4}
              disabled={deaktiviert}
              value={wert}
              onChange={(e) => beiAenderung(e.target.value.replace(/\D/g, ""))}
              fehler={fehler}
            />
          </div>
          <span className="shrink-0 pt-3 text-sm text-muted-foreground-1">Min</span>
          <Knopf
            nurSymbol
            variante="sekundaer"
            groesse="lg"
            type="button"
            label="5 Minuten länger"
            symbol={<Plus />}
            disabled={deaktiviert}
            onClick={() => schritt(1)}
          />
        </div>
      )}
      {frei && keineAngabe && <p className="mt-2 text-sm text-muted-foreground-1">Leer lassen: keine Angabe.</p>}
    </div>
  );
}

/**
 * Endzeitzeile (Betreiber 12.10.2026): ruhige Zeile unter Beginn und Dauer mit Uhr-Symbol, z. B. „09:30 bis 10:15 Uhr“. Der Text
 * kommt fertig vom Aufrufer (Rechnung und Wortlaut stehen in der Fachlogik); leer = nur die (leere) Live-Region bleibt im Dokument,
 * damit Hilfstechnik spätere Änderungen ansagt (`aria-live="polite"`).
 */
export function Endzeitzeile({ id, text }: { id?: string; text: string }) {
  return (
    <p id={id} className="flex items-center gap-x-2 min-h-5 text-sm text-muted-foreground-1" aria-live="polite">
      {text !== "" && <Clock />}
      {text}
    </p>
  );
}
